import { useCallback, useEffect, useState } from 'react';

import { useAuth } from '@/app/auth';
import { auth } from '@/services/firebase';

export interface UseIsAdminResult {
  isAdmin: boolean;
  isLoading: boolean;
  /** Forces a token refresh, needed right after the claim is granted */
  refresh: () => Promise<void>;
}

/**
 * Reads the `admin` custom claim from the ID token. The claim is granted out-of-band via
 * `yarn admin:claim` and only appears after the token refreshes.
 */
export default function useIsAdmin(): UseIsAdminResult {
  const { user, status } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const read = useCallback(async (forceRefresh: boolean) => {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      setIsAdmin(false);
      setIsLoading(false);
      return;
    }

    try {
      const token = await currentUser.getIdTokenResult(forceRefresh);
      setIsAdmin(token.claims.admin === true);
    } catch {
      setIsAdmin(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (status === 'loading') return;
    setIsLoading(true);
    void read(false);
  }, [status, user?.uid, read]);

  const refresh = useCallback(() => read(true), [read]);

  return { isAdmin, isLoading, refresh };
}
